import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';

/**
 * The default-value row for a STRING or NUMBER field, with the "No default (null)" toggle.
 *
 * ⚠ THE TOGGLE MUST BE EXPLICIT, NOT IMPLICIT-BY-ABSENCE. The NUMBER box is `type="number"` with a
 * STATIC attribute, so Angular matches the number value accessor and clearing it writes `null` into
 * the model. Once `null` is meaningful (see field-default.util), "cleared the box" would silently
 * become "chose null", leaving no way to say "default to zero". `onValueInput` normalises that back
 * to 0 whenever the toggle is off.
 *
 * BOOLEAN is deliberately NOT handled here: null is scoped to STRING and NUMBER, and each host
 * dialog keeps its own boolean select with its own option labels.
 *
 * Built once for three dialogs that carry duplicate copies of this row. `inputClass` exists because
 * those dialogs style their inputs differently and this change is not a restyle.
 *
 * See docs/superpowers/specs/2026-09-12-null-for-string-and-number-design.md §5.
 */
@Component({
  selector: 'app-default-value-editor',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './default-value-editor.component.html',
})
export class DefaultValueEditorComponent {
  /** 'STRING' or 'NUMBER'. Anything else renders nothing. */
  @Input() type: string = '';
  @Input() value: any = '';
  @Input() inputClass: string = '';
  @Output() valueChange = new EventEmitter<any>();

  get isNull(): boolean {
    return this.value === null;
  }

  onToggle(checked: boolean): void {
    if (checked) {
      this.valueChange.emit(null);
      return;
    }
    // Unticking restores the type's conventional default, so the field is never left in the
    // "toggle off but value still null" state the toggle exists to make impossible.
    this.valueChange.emit(this.type === 'NUMBER' ? 0 : '');
  }

  onValueInput(raw: any): void {
    if (this.type === 'NUMBER') {
      // The accessor writes null for an empty box. With the toggle off that means zero.
      if (raw === null || raw === undefined || raw === '') {
        this.valueChange.emit(0);
        return;
      }
      const parsed = Number(raw);
      this.valueChange.emit(Number.isFinite(parsed) ? parsed : 0);
      return;
    }
    this.valueChange.emit(raw ?? '');
  }
}
